/* tslint:disable */
/* eslint-disable */
/**
 * Tumelo API
 * This is Tumelo\'s API. It drives increased shareholder engagement by providing transparency of the organizations owned by investors and giving them a voice by allowing them to vote on important issues at these organizations.  The main uses of this API are:  * Transparency - To provide a breakdown to an investor of the organizations in which they are invested. * Voting - To provide the ability for an investor to vote on a poll concerning organizations in which they are invested.  ## Status  This API is currently available as a closed **BETA**. We invite interested parties to [give us feedback](mailto:support@tumelo.com) on this API, its documentation, or anything else about Tumelo\'s transparency service prior to the API becoming generally available.  ## Changelog  A changelog for the api can be found [here](CHANGELOG.md).  ## Access The API is available at the following URL: ``` https://api.prod.tumelo.com/v1 ```  For information on how to obtain access to the API, please [contact us](mailto:support@tumelo.com).  ## Development vs Production Habitats Development and Production environments are separated into their own habitats. Please [contact us](mailto:support@tumelo.com) when you\'re ready to go into production and require access to your company\'s live habitat.  # Using the API  ## Case Sensitivity URLs, headers, path parameters, query parameter names, and query parameter values are all case sensitive. Be careful to use the correct case when constructing requests.  ## Error Handling Requests may return an error indicating that there was a problem responding. The response will contain an HTTP status code as well as a JSON object in the following format which describes the error in more detail: ```json {   \"message\": \"Resource not found\" } ```  ## Pagination Requests that return lists and describe pagination parameters support paginated results. Results are separated into multiple pages, where the size of each page is determined by the `pageSize` query parameter. If omitted, `pageSize` defaults to 50 records. When a response includes a `nextPageToken` in the response body, this token value should be passed as a query parameter `nextPageToken` to the same endpoint in order to retrieve the next page of results. This process should be repeated until the response doesn’t include a `nextPageToken`, indicating that the end of the results has been reached.  When passing the `nextPageToken` parameter, all other filter and query parameters present in the previous request, **must** be filled. Filter parameters *must not* be changed while paginating since this will result in an error or an incorrect overall list. The `nextPageToken` will not be present in the response body if the end of the list has been reached and no further results are available.  ## String Formats The following string formats are used throughout this API.  ### date-time The valid format is date-time notation as defined by [RFC 3339, section 5.6](https://tools.ietf.org/html/rfc3339#section-5.6).  For example: `2020-11-30T10:45:28Z`  # Support If you have any technical questions relating to the Tumelo API, please [contact us](mailto:support@tumelo.com), explaining your question or issue in as much detail as possible, including the operation you were performing and the ISIN or Organization ID you were querying, as well as the results you expected and the actual results or error message you observed. You can expect a response within one business day.  ## Data Issues During development and testing in your development habitat we make available a test data set that includes some sample data constructed to illustrate functionality. It is not intended to be used for any purpose other than testing and/or demonstrating functionality, and should not be relied upon to be an accurate reflection of the underlying holdings of any real fund.  If you have any questions or concerns about the data accuracy or completeness of the data in the production environment, please [report the matter](mailto:support@tumelo.com) to us, giving as much information as possible about the issue.  # Compositions and Breakdowns  An instrument may have a composition, this means it is a fund invested in other instruments.  A breakdown is the bottom level components of a composite instrument or account. By this, we mean organizations and cash that a particular composite instrument or account is invested into either directly or through funds.  Breakdowns also have an `other` listing, this is for funds that only have partial information available, or where an instrument is not mapped to an organization. 
 *
 * The version of the OpenAPI document: 0.145.1
 * Contact: support@tumelo.com
 *
 * NOTE: This class is auto generated by OpenAPI Generator (https://openapi-generator.tech).
 * https://openapi-generator.tech
 * Do not edit the class manually.
 */

import { exists, mapValues } from '../runtime';
import type { Recommendation } from './Recommendation';
import {
    RecommendationFromJSON,
    RecommendationFromJSONTyped,
    RecommendationToJSON,
} from './Recommendation';

/**
 * Voting recommendations are children of a voting policy and provide a vote instruction that the policy recommends should be cast.
 * @export
 * @interface VotingRecommendation
 */
export interface VotingRecommendation {
    /**
     * The identifier of the voting recommendation.
     * @type {string}
     * @memberof VotingRecommendation
     */
    readonly id: string;
    /**
     * The identifier of the voting policy this recommendation is from.
     * @type {string}
     * @memberof VotingRecommendation
     */
    votingPolicyId: string;
    /**
     * The identifier of the organization the proposal belongs to.
     * @type {string}
     * @memberof VotingRecommendation
     */
    organizationId: string;
    /**
     * The identifier of the general meeting the proposal belongs to.
     * @type {string}
     * @memberof VotingRecommendation
     */
    generalMeetingId: string;
    /**
     * The identifier of the proposal the recommendation is for.
     * @type {string}
     * @memberof VotingRecommendation
     */
    proposalId: string;
    /**
     * 
     * @type {Recommendation}
     * @memberof VotingRecommendation
     */
    recommendation: Recommendation;
    /**
     * The time the voting policy recommendation was created at in Tumelo's system.
     * 
     * The valid format is date-time notation as defined by [RFC 3339, section 5.6](https://tools.ietf.org/html/rfc3339#section-5.6).
     * @type {Date}
     * @memberof VotingRecommendation
     */
    readonly createTime: Date;
    /**
     * The time the voting policy recommendation was last updated at.
     * 
     * The valid format is date-time notation as defined by [RFC 3339, section 5.6](https://tools.ietf.org/html/rfc3339#section-5.6).
     * @type {Date}
     * @memberof VotingRecommendation
     */
    readonly updateTime: Date;
}

/**
 * Check if a given object implements the VotingRecommendation interface.
 */
export function instanceOfVotingRecommendation(value: object): boolean {
    let isInstance = true;
    isInstance = isInstance && "id" in value;
    isInstance = isInstance && "votingPolicyId" in value;
    isInstance = isInstance && "organizationId" in value;
    isInstance = isInstance && "generalMeetingId" in value;
    isInstance = isInstance && "proposalId" in value;
    isInstance = isInstance && "recommendation" in value;
    isInstance = isInstance && "createTime" in value;
    isInstance = isInstance && "updateTime" in value;

    return isInstance;
}

export function VotingRecommendationFromJSON(json: any): VotingRecommendation {
    return VotingRecommendationFromJSONTyped(json, false);
}

export function VotingRecommendationFromJSONTyped(json: any, ignoreDiscriminator: boolean): VotingRecommendation {
    if ((json === undefined) || (json === null)) {
        return json;
    }
    return {
        
        'id': json['id'],
        'votingPolicyId': json['votingPolicyId'],
        'organizationId': json['organizationId'],
        'generalMeetingId': json['generalMeetingId'],
        'proposalId': json['proposalId'],
        'recommendation': RecommendationFromJSON(json['recommendation']),
        'createTime': (new Date(json['createTime'])),
        'updateTime': (new Date(json['updateTime'])),
    };
}

export function VotingRecommendationToJSON(value?: VotingRecommendation | null): any {
    if (value === undefined) {
        return undefined;
    }
    if (value === null) {
        return null;
    }
    return {
        
        'votingPolicyId': value.votingPolicyId,
        'organizationId': value.organizationId,
        'generalMeetingId': value.generalMeetingId,
        'proposalId': value.proposalId,
        'recommendation': RecommendationToJSON(value.recommendation),
    };
}

